import React, { useState, useEffect } from "react";
import { 
  getMeusModelosMedicamentos, 
  adicionarModeloMedicamento,
  deletarModeloMedicamento,
  atualizarModeloMedicamento
} from "../../services/authService";
import { useNotifications } from '../../context/NotificationContext';
import { useNavigate } from "react-router-dom";
import "./Relatorio.scss";

const Relatorio = () => {
  const [modelos, setModelos] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [termoBusca, setTermoBusca] = useState("");
  const [modelosFiltrados, setModelosFiltrados] = useState([]);
  const { addNotification } = useNotifications();
  const navigate = useNavigate();

  useEffect(() => {
    carregarModelos();
  }, []);

  useEffect(() => {
    if (termoBusca.trim() === "") {
      setModelosFiltrados(modelos);
    } else {
      const termoLowerCase = termoBusca.toLowerCase();
      const resultados = modelos.filter(modelo => 
        modelo.nome.toLowerCase().includes(termoLowerCase) || 
        modelo.categoria?.toLowerCase().includes(termoLowerCase) ||
        modelo.descricao?.toLowerCase().includes(termoLowerCase) ||
        modelo.fabricante?.toLowerCase().includes(termoLowerCase)
      );
      setModelosFiltrados(resultados);
    }
  }, [termoBusca, modelos]);

  const carregarModelos = async () => {
    try {
      setCarregando(true);
      const data = await getMeusModelosMedicamentos();
      setModelos(data);
      setModelosFiltrados(data);
    } catch (error) {
      console.error("Erro ao carregar modelos:", error);
      addNotification({
        title: "Erro",
        message: "Não foi possível carregar os modelos de medicamentos",
        type: "error"
      });
    } finally {
      setCarregando(false);
    }
  };

  const handleExcluirModelo = async (id) => {
    try {
      await deletarModeloMedicamento(id);
      
      // Adicionar notificação
      addNotification({
        title: "Modelo excluído",
        message: "O modelo de medicamento foi excluído com sucesso",
        type: "success"
      });
      
      await carregarModelos();
    } catch (error) {
      addNotification({
        title: "Erro",
        message: "Não foi possível excluir o modelo de medicamento",
        type: "error"
      });
    }
  };

  const handleEditarModelo = (id) => {
    navigate(`/adicionar-medicamento?editar=${id}`);
  };

  const handleNovoModelo = () => {
    navigate('/adicionar-medicamento');
  };

  const handleChangeBusca = (e) => {
    setTermoBusca(e.target.value);
  };

  return (
    <div className="relatorio-page">
      <div className="page-header">
        <h2>Catálogo de Modelos de Medicamentos</h2>
        <p className="page-description">
          Visualize, pesquise e gerencie seus modelos de medicamentos. Os modelos podem ser usados para facilitar o registro de medicamentos na sua caixa.
        </p>
      </div>
      
      <div className="controles-container">
        <div className="busca-container">
          <div className="campo-busca">
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input 
              type="text" 
              placeholder="Buscar modelos..." 
              value={termoBusca}
              onChange={handleChangeBusca}
            />
            {termoBusca && (
              <button 
                className="limpar-busca" 
                onClick={() => setTermoBusca("")}
                title="Limpar busca"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            )}
          </div>
        </div>
        
        <button 
          className="btn-adicionar" 
          onClick={handleNovoModelo}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Criar Novo Modelo
        </button>
      </div>
      
      {carregando ? (
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Carregando modelos...</p>
        </div>
      ) : modelosFiltrados.length === 0 ? (
        <div className="sem-resultados">
          {termoBusca ? (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <p>Nenhum modelo encontrado para <strong>"{termoBusca}"</strong></p>
              <button className="btn-limpar-busca" onClick={() => setTermoBusca("")}>
                Limpar busca
              </button>
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                <line x1="12" y1="8" x2="12" y2="16"></line>
                <line x1="8" y1="12" x2="16" y2="12"></line>
              </svg>
              <p>Você ainda não possui modelos cadastrados.</p>
              <button className="btn-criar-primeiro" onClick={handleNovoModelo}>
                Criar seu primeiro modelo
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="modelos-grid">
          {modelosFiltrados.map(modelo => (
            <div key={modelo.id} className="modelo-card">
              <div className="modelo-header">
                <div className="modelo-titulo">
                  <h4 className="modelo-nome">{modelo.nome}</h4>
                  {modelo.categoria && (
                    <span className="modelo-categoria">{modelo.categoria}</span>
                  )}
                </div>
                <div className="modelo-acoes">
                  <button 
                    className="btn-editar" 
                    onClick={() => handleEditarModelo(modelo.id)}
                    title="Editar modelo"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                  </button>
                  <button 
                    className="btn-excluir" 
                    onClick={() => {
                      if (window.confirm(`Tem certeza que deseja excluir o modelo "${modelo.nome}"?`)) {
                        handleExcluirModelo(modelo.id);
                      }
                    }}
                    title="Excluir modelo"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 6h18"></path>
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                    </svg>
                  </button>
                </div>
              </div>
              <div className="modelo-detalhes">
                <div className="detalhe-principal">
                  <span className="detalhe-dosagem">{modelo.dosagem}</span>
                  <span className="detalhe-frequencia">{modelo.frequencia}</span>
                </div>
                
                {modelo.fabricante && (
                  <div className="detalhe">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M19 21V5a2 2 0 0 0-2-2H7a2 2 0 0 0-2 2v16"></path>
                      <path d="M1 21h22"></path>
                      <path d="M9 21v-6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v6"></path>
                    </svg>
                    <span className="detalhe-valor">{modelo.fabricante}</span>
                  </div>
                )}
                
                {modelo.viaAdministracao && (
                  <div className="detalhe">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="m12 13-2-2 2-2 2 2-2 2Z"></path>
                      <path d="M12 21.5a9.5 9.5 0 1 1 0-19 9.5 9.5 0 0 1 0 19Z"></path>
                    </svg>
                    <span className="detalhe-valor">{modelo.viaAdministracao}</span>
                  </div>
                )}
                
                {modelo.descricao && (
                  <div className="detalhe descricao-container">
                    <span className="detalhe-valor descricao">{modelo.descricao}</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Relatorio;
